import axios from "axios";
import { v4 as uuid } from "uuid";
import { enqueue, cacheParts, getCachedParts, getCachedPartByNumber } from "@/lib/offline";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("auth_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const branch = localStorage.getItem("active_branch_id");
  if (branch) cfg.headers["X-Branch-Id"] = branch;
  return cfg;
});

// -- Refresh-token auto-rotation --------------------------------------------
// When any request comes back 401, try exactly ONE refresh call and replay
// the original request. Concurrent 401s share the same refresh promise so we
// don't fire N parallel refreshes.
let _refreshInFlight = null;

async function _refreshAccess() {
  if (_refreshInFlight) return _refreshInFlight;
  const rt = localStorage.getItem("refresh_token");
  if (!rt) return null;
  _refreshInFlight = (async () => {
    try {
      const res = await axios.post(`${API}/auth/refresh`, { refresh_token: rt });
      const { token, refresh_token: nrt } = res.data || {};
      if (token) localStorage.setItem("auth_token", token);
      if (nrt) localStorage.setItem("refresh_token", nrt);
      return token || null;
    } catch {
      // Refresh failed → force sign-out state
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

// Cache parts catalog responses on every successful GET so they're browsable offline.
api.interceptors.response.use(
  (res) => {
    try {
      if (res.config.method === "get" && /\/parts(\?|$)/.test(res.config.url || "")) {
        if (Array.isArray(res.data)) cacheParts(res.data);
      }
    } catch {
      // ignore cache failures
    }
    return res;
  },
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    // Only retry on 401 for endpoints that require auth, once per request, and
    // never on the refresh endpoint itself.
    if (status === 401 && !cfg._retried && !/\/auth\/(refresh|login)/.test(cfg.url || "")) {
      cfg._retried = true;
      const newToken = await _refreshAccess();
      if (newToken) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${newToken}`;
        return api.request(cfg);
      }
    }
    return Promise.reject(err);
  },
);

/**
 * Resilient GET helper used by parts-heavy pages.
 * Falls back to IndexedDB cache when the network fails.
 */
export async function getPartsResilient(params) {
  try {
    const { data } = await api.get("/parts", { params });
    return { data, offline: false };
  } catch (err) {
    if (!navigator.onLine || !err.response) {
      const cached = await getCachedParts();
      const q = (params?.q || "").toLowerCase();
      const filtered = q
        ? cached.filter((p) =>
            [p.part_number, p.name, p.manufacturer, p.model, p.variant]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(q)),
          )
        : cached;
      return { data: filtered, offline: true };
    }
    throw err;
  }
}

export async function getPartByNumberResilient(part_number) {
  try {
    const { data } = await api.get(`/parts/by-number/${encodeURIComponent(part_number)}`);
    return data;
  } catch (err) {
    if (!navigator.onLine || !err.response) {
      const p = await getCachedPartByNumber(part_number);
      if (p) return p;
    }
    throw err;
  }
}

/**
 * Run a mutation against the backend OR queue it for replay when offline.
 *
 * opts.method: "post" | "patch" | "delete"
 * opts.url:    relative URL within /api (e.g. "/invoices/direct")
 * opts.body:   request payload
 * opts.label:  human-readable description for the queue UI
 * opts.offline_response: dict merged into `data` when queued so UI can keep flowing
 */
export async function mutateOrQueue({ method, url, body, label, offline_response }) {
  if (navigator.onLine) {
    try {
      const res = await api.request({ method, url, data: body });
      return { ok: true, data: res.data, queued: false };
    } catch (err) {
      const offlineLike = !err.response;
      if (!offlineLike) throw err;
    }
  }
  const item = await enqueue({
    method: method.toUpperCase(),
    url,
    body,
    label,
    idempotency_key: uuid(),
  });
  return {
    ok: true,
    queued: true,
    data: { ...(offline_response || {}), _queued: true, _queue_id: item.id, _idempotency_key: item.idempotency_key },
  };
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return JSON.stringify(d);
}

export const ROLES = {
  admin: "Admin",
  manager: "Manager",
  accountant: "Accountant",
  inventory_manager: "Inventory Manager",
  retail_counter: "Retail Counter",
};

export function fmtCurrency(n) {
  if (n === undefined || n === null || isNaN(n)) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
